/**
 * Shared button primitive — the single source of truth for press feedback,
 * focus rings, elevation, and hit area. Before this, every button was an
 * inline className blob; adopt this so the polish stays consistent.
 *
 * Design notes (see the "make-interfaces-feel-better" skill):
 * - Scale-on-press is exactly `0.96` and only fires when enabled. Pass
 *   `static` to disable it (large/destructive rows where motion distracts).
 * - Filled variants use the `shadow-border` elevation token instead of a
 *   solid border (shadows adapt to light/dark; see index.css).
 * - Transitions name exact properties — never `transition` / `transition: all`.
 * - `expandHitArea` adds a 40×40 invisible pseudo so a 24px icon button meets
 *   the min touch target. It is OPT-IN: in dense table rows two adjacent
 *   expanded hit areas would overlap and steal each other's clicks, so only
 *   enable it on standalone icon buttons (nav bell/help, modal close).
 */
import { forwardRef } from "react";
import type { ButtonHTMLAttributes } from "react";

import { cn } from "../lib/cn";

type Variant = "primary" | "secondary" | "danger" | "warning" | "ghost";
type Size = "xs" | "sm" | "icon";

const VARIANT: Record<Variant, string> = {
  primary:
    "bg-emerald-900/40 text-emerald-200 hover:bg-emerald-900/60 " +
    "shadow-border hover:shadow-border-hover",
  secondary:
    "bg-slate-800 text-slate-200 hover:bg-slate-700 " +
    "shadow-border hover:shadow-border-hover",
  danger:
    "bg-red-900/30 text-red-200 hover:bg-red-900/50 " +
    "shadow-border hover:shadow-border-hover",
  warning:
    "text-amber-300 hover:bg-amber-900/40 " +
    "shadow-border hover:shadow-border-hover",
  // Ghost is a bare affordance (nav links, icon buttons) — no depth ring.
  ghost: "text-slate-300 hover:bg-slate-800 hover:text-slate-100",
};

const SIZE: Record<Size, string> = {
  xs: "rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide",
  sm: "rounded px-2 py-1 text-xs",
  icon: "size-6 rounded",
};

const BASE =
  "inline-flex items-center justify-center gap-1.5 font-medium " +
  "transition-[scale,box-shadow,background-color,color] duration-150 ease-out " +
  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-500 " +
  "disabled:opacity-50 disabled:pointer-events-none";

const PRESS = "active:enabled:scale-[0.96]";

// 40×40 centred hit target for small icon buttons. Opt-in (see file header).
const HIT_AREA =
  "relative after:absolute after:left-1/2 after:top-1/2 after:size-10 " +
  "after:-translate-x-1/2 after:-translate-y-1/2";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  /** Disable scale-on-press (e.g. large or destructive controls). */
  static?: boolean;
  /** Add a 40×40 hit area — only for standalone icon buttons (not dense rows). */
  expandHitArea?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = "secondary",
      size = "sm",
      static: isStatic,
      expandHitArea,
      className,
      type,
      children,
      ...props
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type ?? "button"}
        className={cn(
          BASE,
          VARIANT[variant],
          SIZE[size],
          !isStatic && PRESS,
          expandHitArea && HIT_AREA,
          className,
        )}
        {...props}
      >
        {children}
      </button>
    );
  },
);
